import { useQuery } from '@tanstack/react-query';
import { BaseResponseType, apiClient } from 'shared/api';
import { useExchangeRateStore } from 'store/useExchangeRateStore';

import { type ExchangeRatesResult, exchangeRatesResultSchema } from './exchangeRateSchema';

export const exchangeRatesQueryKey = ['exchange-rates'] as const;

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

const getExchangeRates = async (): Promise<ExchangeRatesResult | null> => {
  const { data } = await apiClient.get('/exchange-rates');
  const result = (data as BaseResponseType<unknown>).result;
  // 백엔드 응답이 예상 구조와 다르면 (필드 누락, 타입 불일치 등) → null 반환
  // → 상위 fallback (localStorage 캐시)이 동작하도록 처리
  const parsed = exchangeRatesResultSchema.safeParse(result);
  if (!parsed.success) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[exchange-rates] response schema mismatch', parsed.error.format());
    }
    return null;
  }
  return parsed.data;
};

export const useGetExchangeRates = () => {
  const setRates = useExchangeRateStore((s) => s.setRates);

  return useQuery({
    queryKey: exchangeRatesQueryKey,
    queryFn: async () => {
      const result = await getExchangeRates();
      // 성공(스키마 검증 통과 + rates 비어있지 않음)에만 store 동기화
      // → 실패/이상 응답 시 직전 캐시(localStorage) 유지
      if (result && result.rates.length > 0) {
        setRates(result.rates, result.updatedAt);
      }
      return result;
    },
    staleTime: ONE_DAY_MS,
    gcTime: ONE_DAY_MS,
    retry: 1,
    refetchOnWindowFocus: false,
  });
};
